import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang, LangToggle } from "./LanguageContext";

const PATRIZIAS_LOGO =
  "https://images.getbento.com/accounts/78540d47b961291fe22c29ae3eb8a893/media/images/12400Patrizias-Brush.png?w=600&auto=compress,format";

const navCopy = {
  en: { show: "The Show", craft: "The Craft", models: "Business Models", events: "Grand Events", gallery: "Gallery", cta: "Request a Presentation" },
  it: { show: "Lo Show", craft: "Il Mestiere", models: "Modelli di Business", events: "Grandi Eventi", gallery: "Galleria", cta: "Richiedi una Presentazione" },
  ru: { show: "Шоу", craft: "Ремесло", models: "Бизнес-модели", events: "Большие события", gallery: "Галерея", cta: "Запросить презентацию" },
  es: { show: "El Show", craft: "El Oficio", models: "Modelos de Negocio", events: "Grandes Eventos", gallery: "Galería", cta: "Solicitar una Presentación" },
};

export const Header = ({ brand = "filata" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang } = useLang();
  const t = navCopy[lang];
  const links = [
    { label: t.show, id: "mozzarella-show" },
    { label: t.craft, id: "craft" },
    { label: t.models, id: "business-models" },
    { label: t.events, id: "grand-events" },
    { label: t.gallery, id: "gallery" },
  ];
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled ? "bg-bg/80 backdrop-blur-xl border-b border-ink/10 shadow-[0_12px_30px_-24px_rgba(43,14,16,0.6)]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-24 flex items-center justify-between">
        <button
          data-testid="logo-button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center"
          aria-label={brand === "patrizias" ? "Patrizia's Home" : "Filata Home"}
        >
          {brand === "patrizias" ? (
            <img src={PATRIZIAS_LOGO} alt="Patrizia's" className="h-12 md:h-14 w-auto" />
          ) : (
            <span className="font-serif text-3xl md:text-4xl tracking-tight text-ink">Filata<span className="text-terracotta">.</span></span>
          )}
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="group relative font-sans text-sm tracking-wide text-ink-soft hover:text-ink transition-colors duration-300"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-full bg-terracotta origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="hidden sm:block">
            <LangToggle />
          </div>
          <button
            data-testid="header-book-button"
            onClick={() => scrollTo("book")}
            className="group relative overflow-hidden rounded-full bg-ink px-5 md:px-6 py-2.5 text-sm font-sans tracking-wide text-bg"
          >
            <span className="relative z-10">{t.cta}</span>
            <span className="absolute inset-0 bg-terracotta scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            className="md:hidden text-ink p-1"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-bg/95 backdrop-blur-xl border-b border-ink/10"
          >
            <div className="px-6 py-5 flex flex-col gap-1">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => scrollTo(l.id)}
                  className="py-3 text-left font-serif text-2xl text-ink border-b border-ink/5"
                >
                  {l.label}
                </button>
              ))}
              <div className="pt-4">
                <LangToggle />
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
